import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  AppPropertyBasicModel,
  BankAccountInfoModel,
  Currency,
  OrganizationModel,
  OrganizationResourceService,
  OrganizationWithUinModel,
  PagedOrganizationWithUinModel,
  SaveBankAccountRequest,
  SaveOrganizationRequest,
  SaveOrgPropertyRequest,
  SaveResult,
} from '../vn-api';

@Injectable({
  providedIn: 'root'
})
export class HxOrganizationService {
  constructor(
    private orgResourceService: OrganizationResourceService,
  ) {
  }

  getOrganizations(params?: {
    page?: number,
    limit?: number,
    includeDisabled?: boolean,
    sort?: string[],
    searchText?: string,
    countryId?: number,
    storeIds?: number[],
  }): Promise<PagedOrganizationWithUinModel> {
    return firstValueFrom(this.orgResourceService.getOrganizations(
      params?.countryId,
      params?.includeDisabled,
      params?.limit,
      params?.page,
      params?.searchText,
      params?.sort,
      params?.storeIds,
    ));
  }

  removeOrganization(id: number): Promise<any> {
    return firstValueFrom(this.orgResourceService.removeOrganizationById(id));
  }

  getOrganizationById(id: number): Promise<OrganizationModel> {
    return firstValueFrom(this.orgResourceService.getOrganizationById(id));
  }

  getOrganizationByStoreId(storeId: number): Promise<OrganizationModel> {
    return firstValueFrom(this.orgResourceService.getOrganizationByStoreId(storeId));
  }

  saveOrganization(req: SaveOrganizationRequest): Promise<SaveResult> {
    return firstValueFrom(this.orgResourceService.saveOrganization(req));
  }

  getPropertiesById(id: number): Promise<AppPropertyBasicModel[]> {
    return firstValueFrom(this.orgResourceService.getOrganizationProperties(id)) as Promise<AppPropertyBasicModel[]>;
  }

  getPropertyByKey(id: number, key: string): Promise<AppPropertyBasicModel | null> {
    return firstValueFrom(this.orgResourceService.getOrganizationProperties(id, key)) as Promise<AppPropertyBasicModel | null>;
  }

  saveProperty(id: number, req: SaveOrgPropertyRequest): Promise<SaveResult> {
    return firstValueFrom(this.orgResourceService.saveOrganizationProperty(id, req));
  }

  removeProperty(orgId: number, id: number): Promise<any> {
    return firstValueFrom(this.orgResourceService.removeOrganizationProperty(id, orgId));
  }

  getPaymentAgreementUploadUrl(orgId: number): string {
    return `/api/vn/organizations/${orgId}/payment-agreement`;
  }

  getDebtAllowed(id: number): Promise<AppPropertyBasicModel> {
    return firstValueFrom(this.orgResourceService.getDebtAllowed(id));
  }

  getMyOrganizations(params: { countryId: number }): Promise<OrganizationWithUinModel[]> {
    return firstValueFrom(this.orgResourceService.getMyOrganizations(params.countryId));
  }

  getBankAccounts(orgId: number): Promise<BankAccountInfoModel[]> {
    return firstValueFrom(this.orgResourceService.getBankAccountsByOrgId(orgId));
  }

  saveBankAccount(id: number, body: {
    id?: number;
    bankId?: number;
    iban: string;
    currency: Currency;
    beneficiaryCode: string;
    isDefault: boolean;
    containerId: number;
  }): Promise<{id: number}> {
    return firstValueFrom(this.orgResourceService.saveOrganizationBankAccount(id, body as SaveBankAccountRequest));
  }

  setDefaultBankAccount(id: number, bankAccountId: number): Promise<void> {
    return firstValueFrom(this.orgResourceService.setDefaultBankAccount(bankAccountId, id));
  }

  deleteBankAccount(id: number, bankAccountId: number): Promise<void> {
    return firstValueFrom(this.orgResourceService.removeOrganizationBankAccount(bankAccountId, id));
  }
}
